/*!
 *
 *  Copyright (c) "Neo4j"
 *  Neo4j Sweden AB [http://neo4j.com]
 *
 *  This file is part of Neo4j.
 *
 *  Neo4j is free software: you can redistribute it and/or modify
 *  it under the terms of the GNU General Public License as published by
 *  the Free Software Foundation, either version 3 of the License, or
 *  (at your option) any later version.
 *
 *  This program is distributed in the hope that it will be useful,
 *  but WITHOUT ANY WARRANTY; without even the implied warranty of
 *  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 *  GNU General Public License for more details.
 *
 *  You should have received a copy of the GNU General Public License
 *  along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */

import '@neo4j-ndl/base/lib/neo4j-ds-styles.css';

import {
  DocumentIconOutline,
  FolderIconSolid,
  PhotoIconOutline,
} from '@neo4j-ndl/react/icons';
import { TreeView } from '@neo4j-ndl/react/next';
import { useCallback, useState } from 'react';

/**
 * Every row owns its own checkbox: `onSelectedChange` reports the state the row
 * should end up in, and the story stores exactly that. Folders are ordinary
 * selectable rows here, unrelated to what sits inside them.
 *
 * Deriving a folder's state from its children instead needs the tree to own the
 * selection, since a Shift range moves several rows at once and a cascade has
 * to be applied to the whole set in one pass. See the root-managed selection
 * story for that.
 */
const Component = () => {
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [selected, setSelected] = useState<Record<string, boolean>>({});

  const toggleExpanded = (key: string) =>
    setExpanded((prev) => ({ ...prev, [key]: !prev[key] }));

  const setRowSelected = useCallback((key: string, isSelected: boolean) => {
    setSelected((prev) => ({ ...prev, [key]: isSelected }));
  }, []);

  return (
    <TreeView ariaLabel="File explorer" selectionMode="multiple">
      <TreeView.Item
        title="Documents"
        hasChildren
        isExpanded={expanded['documents']}
        onExpandedChange={() => toggleExpanded('documents')}
        isSelected={selected['documents'] ?? false}
        onSelectedChange={(isSelected) =>
          setRowSelected('documents', isSelected)
        }
        leadingVisual={<FolderIconSolid />}
      >
        <TreeView.Item
          title="Work"
          hasChildren
          isExpanded={expanded['work']}
          onExpandedChange={() => toggleExpanded('work')}
          isSelected={selected['work'] ?? false}
          onSelectedChange={(isSelected) => setRowSelected('work', isSelected)}
          leadingVisual={<FolderIconSolid />}
        >
          <TreeView.Item
            title="Report.pdf"
            isSelected={selected['report'] ?? false}
            onSelectedChange={(isSelected) =>
              setRowSelected('report', isSelected)
            }
            leadingVisual={<DocumentIconOutline />}
          />
          <TreeView.Item
            title="Presentation.pptx"
            isDisabled
            isSelected={selected['presentation'] ?? false}
            onSelectedChange={(isSelected) =>
              setRowSelected('presentation', isSelected)
            }
            leadingVisual={<DocumentIconOutline />}
          />
        </TreeView.Item>
        <TreeView.Item
          title="Resume.pdf"
          isSelected={selected['resume'] ?? false}
          onSelectedChange={(isSelected) =>
            setRowSelected('resume', isSelected)
          }
          leadingVisual={<DocumentIconOutline />}
        />
      </TreeView.Item>
      <TreeView.Item
        title="Images"
        hasChildren
        isExpanded={expanded['images']}
        onExpandedChange={() => toggleExpanded('images')}
        isSelected={selected['images'] ?? false}
        onSelectedChange={(isSelected) => setRowSelected('images', isSelected)}
        leadingVisual={<FolderIconSolid />}
      >
        <TreeView.Item
          title="Photo.jpg"
          isSelected={selected['photo'] ?? false}
          onSelectedChange={(isSelected) => setRowSelected('photo', isSelected)}
          leadingVisual={<PhotoIconOutline />}
        />
        <TreeView.Item
          title="Screenshot.png"
          isSelected={selected['screenshot'] ?? false}
          onSelectedChange={(isSelected) =>
            setRowSelected('screenshot', isSelected)
          }
          leadingVisual={<PhotoIconOutline />}
        />
      </TreeView.Item>
    </TreeView>
  );
};

export default Component;
